// StudentProfileModal.js

import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import StudentTimeLogsDetails from "./StudentTimeLogsDetails";
import TrainingPlanForm from "./TrainingPlanForm";

const StudentProfileModal = ({ student, onClose, opportunities, timeLogs }) => {
  const [studentOpportunities, setStudentOpportunities] = useState([]);
  const [studentTimeLogs, setStudentTimeLogs] = useState([]);
  const [activeTab, setActiveTab] = useState("student");

  useEffect(() => {
    const filteredOpportunities = opportunities.filter(
      (opportunity) =>
        opportunity.students && opportunity.students.includes(student.studentId)
    );
    setStudentOpportunities(filteredOpportunities);

    const filteredTimeLogs = timeLogs.filter(
      (log) => log.studentId === student.studentId
    );
    setStudentTimeLogs(filteredTimeLogs);
  }, [student, opportunities, timeLogs]);

  const handleOutsideClick = (e) => {
    if (e.target.classList.contains('modal-overlay')) {
      onClose();
    }
  };

  if (!student) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case "student":
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-full bg-blue-500 flex items-center justify-center text-white text-xl font-medium">
                {`${student.firstName?.[0]}${student.lastName?.[0]}`}
              </div>
              <div>
                <h3 className="text-lg font-semibold">Student Information</h3>
                <p className="text-gray-500">{student.email}</p>
              </div>
            </div>
            <p>
              <strong>Student ID:</strong> {student.studentId || "N/A"}
            </p>
            <p>
              <strong>Student name:</strong>{" "}
              {`${student.firstName} ${student.lastName}` || "N/A"}
            </p>
            <p>
              <strong>Cohort:</strong> {student.cohort || "N/A"}
            </p>
            <p>
              <strong>Program:</strong> {student.program || "N/A"}
            </p>
          </div>
        );
      case "address":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">
              Contact & Address Information
            </h3>
            <p>
              <strong>Email:</strong> {student.email || "N/A"}
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              {student.phone ? (
                <a href={`tel:${student.phone}`} className="text-blue-600 hover:text-blue-800">
                  {student.phone}
                </a>
              ) : (
                "N/A"
              )}
            </p>
            <p>
              <strong>Street:</strong> {student.address?.street || "N/A"}
            </p>
            <p>
              <strong>City:</strong> {student.address?.city || "N/A"}
            </p>
            <p>
              <strong>State:</strong> {student.address?.state || "N/A"}
            </p>
            <p>
              <strong>Zip:</strong> {student.address?.zip || "N/A"}
            </p>
          </div>
        );
      case "parent":
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Parent Information</h3>
            <p>
              {student.parentName || "N/A"} - {" "}
              {student.parentPhone ? (
                <a href={`tel:${student.parentPhone}`} className="text-blue-600 hover:text-blue-800">
                  {student.parentPhone}
                </a>
              ) : (
                "N/A"
              )}
            </p>
          </div>
        );
      case "opportunities":
        return (
          <StudentTimeLogsDetails
            studentTimeLogs={studentTimeLogs}
            studentOpportunities={studentOpportunities}
            student={student}
          />
        );
      case "training":
        return (
          <TrainingPlanForm student={student} onClose={onClose} />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="student-profile-title"
      onClick={handleOutsideClick}
    >
      <div
        className={`bg-white rounded-lg w-full mx-4 ${
          activeTab === "opportunities"
            ? "max-w-4xl h-[90vh]"
            : "max-w-2xl h-[90vh] md:h-[70vh]"
        } p-4 md:p-6 relative flex flex-col transition-all duration-200 overflow-y-auto`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
          aria-label="Close Modal"
        >
          <X size={20} />
        </button>
        {/* Modal Title */}
        <div className="flex items-center justify-between mb-6">
          <h2 id="student-profile-title" className="text-2xl font-bold">
            {student.firstName} {student.lastName}'s Profile
          </h2>
        </div>

        {/* Navigation Tabs - Keep this section fixed */}
        <div className="mb-6">
          <nav className="flex space-x-4 border-b">
            {["student", "address", "parent", "opportunities", "training"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`py-2 px-4 ${
                  activeTab === tab
                    ? "border-b-2 border-blue-600 text-blue-600"
                    : "text-gray-600 hover:text-blue-600"
                }`}
              >
                {tab === "training" ? "Training Plan" : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Area - Make this section scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6">{renderTabContent()}</div>
        </div>

        {/* Footer with Close Button - Keep this section fixed */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentProfileModal;